import React, { useState, useEffect, useCallback } from 'react';
import { PlusIcon, EyeIcon, CreditCardIcon, ArchiveBoxIcon, XMarkIcon } from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import CustomSelect from '../../components/ui/CustomSelect';

const POSPurchases: React.FC = () => {
  const [purchases, setPurchases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [receiveTarget, setReceiveTarget] = useState<any>(null);
  const [receiving, setReceiving] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<any>(null);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [filters, setFilters] = useState({ status: '', supplier_id: '' });

  const [createForm, setCreateForm] = useState({
    supplier_id: '',
    purchase_date: new Date().toISOString().split('T')[0],
    discount_amount: 0,
    notes: '',
    initial_payment: 0,
    payment_method: 'cash',
    payment_status: 'unpaid' as 'unpaid' | 'partial' | 'paid',
    items: [{ product_id: '', variant_id: '', size_name: '', quantity: 1, cost_price: 0 }],
  });

  const [paymentForm, setPaymentForm] = useState({ amount: 0, payment_method: 'cash', reference_number: '', notes: '' });

  const fetchPurchases = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { per_page: 30 };
      if (filters.status) params.status = filters.status;
      if (filters.supplier_id) params.supplier_id = filters.supplier_id;
      const response = await apiService.get('/pos/purchases', { params });
      setPurchases(response.data || []);
    } catch (error) {
      console.error('Failed to load purchases:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchSuppliers = async () => {
    try {
      const response = await apiService.get('/pos/suppliers/list');
      setSuppliers(response || []);
    } catch {}
  };

  const fetchProducts = async () => {
    try {
      const response = await apiService.get('/pos/products', { params: { per_page: 200 } });
      setProducts(response.data || []);
    } catch {}
  };

  const fetchStats = async () => {
    try {
      const response = await apiService.get('/pos/purchases/statistics');
      setStats(response);
    } catch {}
  };

  useEffect(() => {
    fetchPurchases();
    fetchSuppliers();
    fetchProducts();
    fetchStats();
  }, [fetchPurchases]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const itemsTotal = createForm.items.filter(i => i.product_id).reduce((sum, i) => sum + Number(i.quantity) * Number(i.cost_price), 0);
      let initialPayment = 0;
      if (createForm.payment_status === 'paid') {
        initialPayment = itemsTotal - Number(createForm.discount_amount);
      } else if (createForm.payment_status === 'partial') {
        initialPayment = Number(createForm.initial_payment);
      }

      const payload = {
        supplier_id: Number(createForm.supplier_id),
        purchase_date: createForm.purchase_date,
        discount_amount: Number(createForm.discount_amount) || 0,
        notes: createForm.notes || null,
        initial_payment: initialPayment,
        payment_method: createForm.payment_method,
        items: createForm.items.filter(i => i.product_id).map(i => ({
          product_id: Number(i.product_id),
          variant_id: i.variant_id ? Number(i.variant_id) : null,
          size_name: i.size_name || null,
          quantity: Number(i.quantity),
          cost_price: Number(i.cost_price),
        })),
      };
      await apiService.post('/pos/purchases', payload);
      setShowCreateModal(false);
      setCreateForm({ supplier_id: '', purchase_date: new Date().toISOString().split('T')[0], discount_amount: 0, notes: '', initial_payment: 0, payment_method: 'cash', payment_status: 'unpaid', items: [{ product_id: '', variant_id: '', size_name: '', quantity: 1, cost_price: 0 }] });
      fetchPurchases();
      fetchStats();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to create purchase');
    }
  };

  const openReceiveModal = (purchase: any) => {
    setReceiveTarget(purchase);
    setShowReceiveModal(true);
  };

  const confirmReceive = async () => {
    if (!receiveTarget) return;
    setReceiving(true);
    try {
      await apiService.post(`/pos/purchases/${receiveTarget.id}/receive`);
      setShowReceiveModal(false);
      setReceiveTarget(null);
      fetchPurchases();
      fetchStats();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to receive purchase');
    } finally {
      setReceiving(false);
    }
  };

  const handlePayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPurchase) return;
    try {
      await apiService.post(`/pos/purchases/${selectedPurchase.id}/payment`, paymentForm);
      setShowPaymentModal(false);
      setPaymentForm({ amount: 0, payment_method: 'cash', reference_number: '', notes: '' });
      fetchPurchases();
      fetchStats();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to record payment');
    }
  };

  const handleViewDetail = async (id: number) => {
    try {
      const response = await apiService.get(`/pos/purchases/${id}`);
      setSelectedPurchase(response);
      setShowDetailModal(true);
    } catch {}
  };

  const openPaymentModal = (purchase: any) => {
    setSelectedPurchase(purchase);
    setPaymentForm({ amount: Number(purchase.due_amount), payment_method: 'cash', reference_number: '', notes: '' });
    setShowPaymentModal(true);
  };

  const addItem = () => {
    setCreateForm(f => ({ ...f, items: [...f.items, { product_id: '', variant_id: '', size_name: '', quantity: 1, cost_price: 0 }] }));
  };

  const updateItem = (index: number, field: string, value: any) => {
    setCreateForm(f => ({
      ...f,
      items: f.items.map((item, i) => {
        if (i !== index) return item;
        const updated = { ...item, [field]: value };
        // Reset dependent fields
        if (field === 'product_id') {
          updated.variant_id = '';
          updated.size_name = '';
        }
        if (field === 'variant_id') {
          updated.size_name = '';
        }
        return updated;
      }),
    }));
  };

  const removeItem = (index: number) => {
    setCreateForm(f => ({ ...f, items: f.items.filter((_, i) => i !== index) }));
  };

  // Get variants for a product
  const getProductVariants = (productId: string) => {
    if (!productId) return [];
    const product = products.find((p: any) => p.id === Number(productId));
    return product?.variants || [];
  };

  // Get sizes for a variant
  const getVariantSizes = (productId: string, variantId: string) => {
    const variants = getProductVariants(productId);
    const variant = variants.find((v: any) => v.id === Number(variantId));
    return variant?.sizes || [];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Purchase Management</h1>
          <p className="text-gray-600">Manage purchases from suppliers</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="bg-gray-500 text-white px-3 py-1.5 text-sm rounded-md hover:bg-gray-600 flex items-center space-x-1.5">
          <PlusIcon className="h-4 w-4" />
          <span>New Purchase</span>
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm font-medium text-gray-600">Total Due</p>
            <p className="text-2xl font-bold text-red-600">{Number(stats.total_due).toLocaleString()} MMK</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm font-medium text-gray-600">Unpaid</p>
            <p className="text-2xl font-bold text-yellow-600">{stats.unpaid}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm font-medium text-gray-600">Partially Paid</p>
            <p className="text-2xl font-bold text-orange-600">{stats.partial}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm font-medium text-gray-600">This Month</p>
            <p className="text-2xl font-bold text-gray-900">{Number(stats.this_month_purchases).toLocaleString()} MMK</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow flex gap-3">
        <CustomSelect
          value={filters.status}
          onChange={(val) => setFilters(f => ({ ...f, status: val }))}
          placeholder="All Status"
          options={[
            { value: '', label: 'All Status' },
            { value: 'unpaid', label: 'Unpaid' },
            { value: 'partial', label: 'Partial' },
            { value: 'paid', label: 'Paid' },
          ]}
          className="w-40"
        />
        <CustomSelect
          value={filters.supplier_id}
          onChange={(val) => setFilters(f => ({ ...f, supplier_id: val }))}
          placeholder="All Suppliers"
          options={[
            { value: '', label: 'All Suppliers' },
            ...suppliers.map((s: any) => ({ value: String(s.id), label: s.name })),
          ]}
          className="w-48"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Purchase #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Paid</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Due</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Received</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={9} className="px-6 py-8 text-center text-gray-500">Loading...</td></tr>
            ) : purchases.length === 0 ? (
              <tr><td colSpan={9} className="px-6 py-8 text-center text-gray-500">No purchases found</td></tr>
            ) : (
              purchases.map((purchase) => (
                <tr key={purchase.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-mono text-gray-900">{purchase.purchase_number}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{purchase.supplier?.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{purchase.purchase_date}</td>
                  <td className="px-6 py-4 text-sm text-right text-gray-900 font-medium">{Number(purchase.total_amount).toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-right text-green-600">{Number(purchase.paid_amount).toLocaleString()}</td>
                  <td className="px-6 py-4 text-sm text-right text-red-600 font-medium">{Number(purchase.due_amount).toLocaleString()}</td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      purchase.status === 'paid' ? 'bg-green-100 text-green-800' :
                      purchase.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>{purchase.status}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {purchase.received_date ? (
                      <span className="text-green-600 text-xs font-medium">✓ Received</span>
                    ) : (
                      <button onClick={() => openReceiveModal(purchase)} className="inline-flex items-center px-2 py-1 bg-green-50 text-green-700 text-xs font-medium rounded hover:bg-green-100">
                        <ArchiveBoxIcon className="h-3 w-3 mr-1" />Receive
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center space-x-1">
                    <button onClick={() => handleViewDetail(purchase.id)} className="text-indigo-600 hover:text-indigo-900"><EyeIcon className="h-4 w-4 inline" /></button>
                    {purchase.status !== 'paid' && (
                      <button onClick={() => openPaymentModal(purchase)} className="text-green-600 hover:text-green-900"><CreditCardIcon className="h-4 w-4 inline" /></button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Purchase Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">New Purchase Order</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Supplier *</label>
                  <select required value={createForm.supplier_id} onChange={(e) => setCreateForm(f => ({ ...f, supplier_id: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                    <option value="">Select Supplier</option>
                    {suppliers.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date *</label>
                  <input type="date" required value={createForm.purchase_date} onChange={(e) => setCreateForm(f => ({ ...f, purchase_date: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                </div>
              </div>

              {/* Items */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">Items</label>
                  <button type="button" onClick={addItem} className="text-sm text-indigo-600 hover:text-indigo-800">+ Add Item</button>
                </div>
                <div className="space-y-2">
                  {createForm.items.map((item, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-md p-3 space-y-2">
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <select value={item.product_id} onChange={(e) => updateItem(idx, 'product_id', e.target.value)} className="col-span-11 px-2 py-1.5 border border-gray-300 rounded text-sm">
                          <option value="">Select Product</option>
                          {products.map((p: any) => <option key={p.id} value={p.id}>{p.name} ({p.product_code})</option>)}
                        </select>
                        <button type="button" onClick={() => removeItem(idx)} className="col-span-1 text-red-500 hover:text-red-700 text-center"><XMarkIcon className="h-4 w-4 inline" /></button>
                      </div>
                      {item.product_id && getProductVariants(item.product_id).length > 0 && (
                        <div className="grid grid-cols-2 gap-2">
                          <select value={item.variant_id} onChange={(e) => updateItem(idx, 'variant_id', e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded text-sm">
                            <option value="">Select Color/Variant</option>
                            {getProductVariants(item.product_id).map((v: any) => (
                              <option key={v.id} value={v.id}>{v.color} ({v.sku})</option>
                            ))}
                          </select>
                          {item.variant_id && (
                            <select value={item.size_name} onChange={(e) => updateItem(idx, 'size_name', e.target.value)} className="px-2 py-1.5 border border-gray-300 rounded text-sm">
                              <option value="">Select Size</option>
                              {getVariantSizes(item.product_id, item.variant_id).map((s: any) => (
                                <option key={s.name} value={s.name}>{s.name} (stock: {s.stock_quantity})</option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs text-gray-500">Quantity</label>
                          <input type="number" min="1" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Cost/Unit (MMK)</label>
                          <input type="number" min="0" step="1" value={item.cost_price || ''} onChange={(e) => updateItem(idx, 'cost_price', e.target.value)} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">Line Total</label>
                          <div className="px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-sm font-medium text-gray-700">
                            {(Number(item.quantity) * Number(item.cost_price)).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount</label>
                  <input type="number" min="0" value={createForm.discount_amount || ''} onChange={(e) => setCreateForm(f => ({ ...f, discount_amount: Number(e.target.value) }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="0" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <input type="text" value={createForm.notes} onChange={(e) => setCreateForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
                </div>
              </div>

              {/* Payment Status */}
              <div className="border-t pt-4 mt-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
                <div className="flex gap-3">
                  {(['unpaid', 'partial', 'paid'] as const).map(status => (
                    <label key={status} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition ${
                      createForm.payment_status === status
                        ? status === 'paid' ? 'border-green-500 bg-green-50 text-green-700' : status === 'partial' ? 'border-yellow-500 bg-yellow-50 text-yellow-700' : 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}>
                      <input type="radio" name="payment_status" value={status} checked={createForm.payment_status === status} onChange={(e) => setCreateForm(f => ({ ...f, payment_status: e.target.value as any }))} className="sr-only" />
                      <span className="text-sm font-medium capitalize">{status === 'partial' ? 'Partially Paid' : status === 'paid' ? 'Fully Paid' : 'Unpaid'}</span>
                    </label>
                  ))}
                </div>
                {createForm.payment_status === 'partial' && (
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Amount Paid (MMK)</label>
                      <input type="number" min="0" value={createForm.initial_payment || ''} onChange={(e) => setCreateForm(f => ({ ...f, initial_payment: Number(e.target.value) }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Enter amount" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Payment Method</label>
                      <select value={createForm.payment_method} onChange={(e) => setCreateForm(f => ({ ...f, payment_method: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                        <option value="cash">Cash</option>
                        <option value="card">Card</option>
                        <option value="bank_transfer">Bank Transfer</option>
                        <option value="qr_payment">QR Payment</option>
                      </select>
                    </div>
                  </div>
                )}
                {createForm.payment_status === 'paid' && (
                  <div className="mt-3">
                    <label className="block text-xs text-gray-600 mb-1">Payment Method</label>
                    <select value={createForm.payment_method} onChange={(e) => setCreateForm(f => ({ ...f, payment_method: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                      <option value="cash">Cash</option>
                      <option value="card">Card</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="qr_payment">QR Payment</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="text-right text-gray-900 font-bold border-t pt-3">
                Total: {createForm.items.reduce((sum, i) => sum + Number(i.quantity) * Number(i.cost_price), 0).toLocaleString()} MMK
              </div>

              <div className="flex justify-end space-x-3">
                <button type="button" onClick={() => setShowCreateModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700">Create Purchase</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Receive Confirmation Modal */}
      {showReceiveModal && receiveTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-sm p-6 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <ArchiveBoxIcon className="h-5 w-5 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900">Receive purchase?</h3>
                <p className="text-sm text-gray-500 mt-1">
                  This will add all items from purchase{' '}
                  <span className="font-medium text-gray-700">{receiveTarget.purchase_number}</span>{' '}
                  to your inventory stock. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => { setShowReceiveModal(false); setReceiveTarget(null); }}
                disabled={receiving}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmReceive}
                disabled={receiving}
                className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50 inline-flex items-center gap-1.5"
              >
                {receiving ? 'Receiving...' : (<><ArchiveBoxIcon className="h-4 w-4" />Receive to inventory</>)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedPurchase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-sm p-6 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Record Payment</h3>
              <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              {selectedPurchase.purchase_number} — Due: <span className="text-red-600 font-bold">{Number(selectedPurchase.due_amount).toLocaleString()} MMK</span>
            </p>
            <form onSubmit={handlePayment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
                <input type="number" required min="1" max={Number(selectedPurchase.due_amount)} value={paymentForm.amount || ''} onChange={(e) => setPaymentForm(f => ({ ...f, amount: Number(e.target.value) }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                <select value={paymentForm.payment_method} onChange={(e) => setPaymentForm(f => ({ ...f, payment_method: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm">
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="qr_payment">QR Payment</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference #</label>
                <input type="text" value={paymentForm.reference_number} onChange={(e) => setPaymentForm(f => ({ ...f, reference_number: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={() => setShowPaymentModal(false)} className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700">Record Payment</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedPurchase && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-lg p-6 shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">{selectedPurchase.purchase_number}</h3>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-500">Supplier:</span> <span className="font-medium">{selectedPurchase.supplier?.name}</span></div>
                <div><span className="text-gray-500">Date:</span> <span className="font-medium">{selectedPurchase.purchase_date}</span></div>
                <div><span className="text-gray-500">Status:</span> <span className={`font-medium capitalize ${selectedPurchase.status === 'paid' ? 'text-green-600' : selectedPurchase.status === 'partial' ? 'text-yellow-600' : 'text-red-600'}`}>{selectedPurchase.status}</span></div>
                <div><span className="text-gray-500">Total:</span> <span className="font-medium">{Number(selectedPurchase.total_amount).toLocaleString()} MMK</span></div>
                <div><span className="text-gray-500">Paid:</span> <span className="font-medium text-green-600">{Number(selectedPurchase.paid_amount).toLocaleString()} MMK</span></div>
                <div><span className="text-gray-500">Due:</span> <span className="font-medium text-red-600">{Number(selectedPurchase.due_amount).toLocaleString()} MMK</span></div>
              </div>

              <div className="border-t pt-3 mt-3">
                <h4 className="font-medium text-gray-900 mb-2">Items</h4>
                {selectedPurchase.items?.map((item: any) => (
                  <div key={item.id} className="flex justify-between py-1">
                    <span className="text-gray-600">{item.product?.name} × {item.quantity}</span>
                    <span className="font-medium">{Number(item.total).toLocaleString()} MMK</span>
                  </div>
                ))}
              </div>

              {selectedPurchase.payments?.length > 0 && (
                <div className="border-t pt-3 mt-3">
                  <h4 className="font-medium text-gray-900 mb-2">Payment History</h4>
                  {selectedPurchase.payments.map((p: any) => (
                    <div key={p.id} className="flex justify-between py-1">
                      <span className="text-gray-600">{new Date(p.paid_at).toLocaleDateString()} ({p.payment_method})</span>
                      <span className="font-medium text-green-600">{Number(p.amount).toLocaleString()} MMK</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POSPurchases;
